import { loadDataCredentials, loadVendors } from 'js/actions';
import {
  CONNECTION_CATEGORY_DATABASE,
  CONNECTION_CATEGORY_FILE,
  CONNECTION_CATEGORY_NOSQL,
  CONNECTION_CATEGORY_REST,
  CONNECTION_CATEGORY_TEMPLATIZED_API,
} from 'js/constants/connectionCategories';
import { FORM_TYPE_CREATE_DIRECT_FLOW } from 'js/constants/formTypes';
import { ACLQueryModeTypes } from 'js/constants/visibilityFilters';
import { getConnectionOrVendorType } from 'js/lib/ResourceUtils';
import useFlowsAdapter from 'new/components/flows/adapter/useFlowsAdapter';
import { initializeFormToNoSQLCategory } from 'new/components/flows/configure/NoSQL/NoSQL.utils';
import createRestAPIDestinationService from 'new/components/flows/configure/RestAPIDestination/service/createRestAPIDestinationService';
import useFlowsSetup from 'new/components/flows/controller/useFlowsSetup';
import createFlowService from 'new/components/flows/service/createFlowService';
import {
  getActiveItems,
  getVendorEndpointField,
} from 'new/components/flows/types/DirectFlow/utils';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import useCredentials from 'new/state/credentials/repository/useCredentials';
import { useDataSamplesAdapter } from 'new/state/data_samples/adapter/useDataSamplesAdapter';
import { useDataSetAdapter } from 'new/state/data_set/adapter/useDataSetAdapter';
import useSchemas from 'new/state/dataSetSchemas/useSchemas';
import useNexsetsAdapter from 'new/state/nexsets/useNexsetsAdapter';
import usePaging from 'new/state/paging/repository/usePaging';
import usePipelinesAdapter from 'new/state/pipelines/adapter/usePipelinesAdapter';
import useSourcesAdapter from 'new/state/sources/adapter/useSourcesAdapter';
import useVendorsAdapter from 'new/state/vendors/adapter/useVendorsAdapter';
import { useVendors } from 'new/state/vendors/repository/useVendors';
import { tryCatch, tryCatchAsync } from 'new/utils/TryCatch';
import { useCallback, useEffect, useRef, useState } from 'react';
import useAppDispatch from 'js/hooks/useAppDispatch';
import useFlowType from './useFlowType';
import useFlowParams from 'new/components/flows/useFlowParams';
import useQueryParams from 'new/hooks/useQueryParams';
import useResourceEditProvider from 'js/components/ResourceEditProvider/useResourceEditProvider';

const FORM = FORM_TYPE_CREATE_DIRECT_FLOW;
const aclQueryMode = ACLQueryModeTypes.MODE_COLLABORATOR.key;

const flowService = createFlowService();
const restAPIDestinationService = createRestAPIDestinationService();

type UseHandleParams = {
  flowId?: string;
  nexsetId?: string;
  sinkId?: number;
};

const useHandleParams = (): UseHandleParams => {
  // nexsetId is now passed as a prop through FlowParamsProvider (replacing the
  // older URL queryParams path). Read it from useFlowParams for the modal case,
  // and keep URL fallback for non-modal direct routes.
  const { id: sinkId, isModal, nexsetId: propNexsetId } = useFlowParams();
  const { queryParams } = useResourceEditProvider();
  const { flowId, nexsetId: urlNexsetId } = useQueryParams();

  const params = isModal
    ? { ...queryParams, nexsetId: propNexsetId ?? queryParams?.nexsetId }
    : { flowId, nexsetId: urlNexsetId };

  return { ...params, sinkId: Number(sinkId) };
};

export const useStartSink = () => {
  const flowType = useFlowType();
  const [status, setStatus] = useState(null);
  const { flowId, nexsetId, sinkId } = useHandleParams();

  const org = useCurrentOrg();
  const dispatch = useAppDispatch();
  const {
    getSinkConfigFields,
    getMetaInfoById,
    getConnectionCategoryByConnectorType,
    getAuthFields,
  } = usePaging();
  const { data: formData, updateFormData } = useFlowsSetup(FORM, flowType);
  const { vendors } = useVendors();
  const { credentials } = useCredentials();
  const { getNexsetSchemaById } = useSchemas();

  /**
   * @Adapters
   */
  const { requestNexset } = useNexsetsAdapter();
  const { requestResources } = useFlowsAdapter();
  const { getSourceById } = useSourcesAdapter();
  const { requestFullVendor } = useVendorsAdapter();
  const { getPipelineById } = usePipelinesAdapter();
  const { fetchDataSamples } = useDataSamplesAdapter();
  const { requestDataSetById } = useDataSetAdapter();

  const startCreateSink = async () => {
    if (!flowId) return;

    const {
      error: pipelineError,
      data: pipeline,
    } = // get flow
      await tryCatchAsync(getPipelineById(Number(flowId)));

    if (pipelineError || !pipeline?.data_source_id) {
      if (pipelineError) console.error(pipelineError.message);
      return;
    }

    const {
      error: sourceError,
      data: source,
    } = // get source
      await tryCatchAsync(getSourceById(pipeline.data_source_id));

    if (sourceError) {
      console.error(sourceError.message);
      return;
    }

    // Prefer the explicit nexsetId; fall back to the source's first data_set when
    // the nexset hasn't been loaded into the store yet (e.g. BYO canvas).
    const resolvedNexsetId =
      nexsetId ?? (source?.data_sets?.[0] as any)?.id ?? source?.data_sets?.[0] ?? null;

    const { error: nexsetError, data: nexset } =
      resolvedNexsetId != null
        ? await tryCatchAsync(requestNexset(resolvedNexsetId))
        : { error: null, data: null };

    if (nexsetError) {
      console.error(nexsetError.message);
      return;
    }

    if (nexset?.id) {
      requestDataSetById(nexset.id);
      fetchDataSamples(nexset.id);
    }

    setStatus('initialize-create-form');
    updateFormData({ flow: pipeline, nexset, source });
  };

  const initializeCreateForm = async () => {
    const { nexset, selectedConnection: connection } = formData;
    const connectionCategory = getConnectionCategoryByConnectorType(connection);
    const sinkConfigFields = getSinkConfigFields(connection, flowType);
    const dataSchema = nexset ? getNexsetSchemaById(nexset.id) : null;
    const { allowSinkAutoMapping } = getMetaInfoById(connection);

    let sinkConfig = flowService.createDefaultSinkConfig({
      sinkConfig: { ...formData?.sinkConfig },
      orgId: org.id,
      buildNew: false,
      isEditMode: false,
      allowSinkAutoMapping,
      dataSchema: dataSchema ?? null,
      configFields: sinkConfigFields,
      selectedConnection: connection,
      connectionCategory: connectionCategory,
    });

    switch (connectionCategory) {
      case CONNECTION_CATEGORY_TEMPLATIZED_API: {
        const endpointField = sinkConfigFields.find(getVendorEndpointField);

        if (endpointField?.options?.length) {
          const [{ value }] = endpointField.options;
          sinkConfig = {
            ...sinkConfig,
            sink_type: connection,
            vendor_endpoint_id: value,
            scriptCredentialsData: {},
          };
        }

        const { error, data } = tryCatch(() =>
          restAPIDestinationService.getInitialSinkConfig({
            sinkConfig,
            dataSchema,
            connectionCategory: CONNECTION_CATEGORY_TEMPLATIZED_API,
          }),
        );

        if (error) console.warn(error.message);

        updateFormData({
          ...data,
          connectionCategory,
          dataSetId: nexset?.id,
          scriptCredentialsData: {},
          selectedConnection: connection,
        });

        const effectiveConnectionType =
          formData.selectedCredential._vendorConnectionType || connection;
        const connectionAuth = getAuthFields(effectiveConnectionType);
        const { templates, items } = connectionAuth;
        const connectionAuthFields = getActiveItems(templates, items, undefined);
        updateFormData({ connectionAuthFields });

        break;
      }

      case CONNECTION_CATEGORY_REST: {
        const { error, data } = tryCatch(() =>
          restAPIDestinationService.getInitialSinkConfig({
            sinkConfig,
            dataSchema,
          }),
        );

        if (error) console.warn(error.message);

        updateFormData({
          ...formData,
          ...data,
          dataSetId: nexset?.id,
          scriptCredentialsData: {},
          selectedConnection: connection,
        });
        break;
      }

      case CONNECTION_CATEGORY_DATABASE: {
        const { error, data } = tryCatch(() =>
          flowService.getInitialSinkConfigToDatabaseConnector({
            sink: null,
            dataSchema,
            sinkConfig,
            connectionCategory,
          }),
        );

        if (error) console.warn(error.message);

        updateFormData({ ...data });
        break;
      }

      default:
        break;
    }
  };

  const startEditSink = async () => {
    if (!sinkId) return;
    const param = { resourceType: 'sink', id: sinkId, flowId };
    const { error, data } = await tryCatchAsync(requestResources(param));
    const { sink, source, nexset } = data || {};
    if (error) {
      console.error(error.message);
      return;
    }

    const sinkConnectionOrVendorType = sink?.sink_type
      ? getConnectionOrVendorType(sink, vendors, 'sink_type', 'vendor_id')
      : null;

    const { error: vendorRequestError } = await tryCatchAsync(
      requestFullVendor(sinkConnectionOrVendorType),
    );

    if (vendorRequestError) {
      console.error(vendorRequestError.message);
      return;
    }

    setStatus('initialize-edit-form');
    updateFormData({ sink, nexset, source });
    await Promise.all([requestDataSetById(nexset.id), fetchDataSamples(nexset.id)]);
  };

  const sink = formData?.sink;
  const nexset = formData?.nexset;
  const savedSinkMapping = formData?.sinkMapping;

  const initializeEditForm = useCallback(async () => {
    if (!sinkId) return;

    try {
      const connection = sink?.sink_type
        ? getConnectionOrVendorType(sink, vendors, 'sink_type', 'vendor_id')
        : null;

      const dataSchema = getNexsetSchemaById(nexset.id);
      const { allowSinkAutoMapping } = getMetaInfoById(connection);
      const sinkConfigFields = getSinkConfigFields(connection, flowType);
      const connectionCategory = getConnectionCategoryByConnectorType(connection);

      let sinkConfig = sink?.['sink_config'];
      let sinkMapping = savedSinkMapping;
      const credID = sink?.data_credentials;
      const selectedCredential = credentials[credID];

      let initFormData: any = {
        sinkConfig,
        connectionCategory,
        selectedCredential,
        credentialId: credID,
        selectedConnection: connection,
      };

      const { error: errorDefaultSinkConfig, data: defaultSinkConfig } = tryCatch(() =>
        flowService.createDefaultSinkConfig({
          sinkConfig,
          orgId: org.id,
          buildNew: false,
          isEditMode: true,
          allowSinkAutoMapping,
          dataSchema: dataSchema,
          configFields: sinkConfigFields,
          selectedConnection: connection,
          connectionCategory: connectionCategory,
        }),
      );

      if (errorDefaultSinkConfig) {
        console.warn(errorDefaultSinkConfig.message);
        return;
      }

      switch (connectionCategory) {
        case CONNECTION_CATEGORY_TEMPLATIZED_API: {
          const { error, data } = tryCatch(() =>
            restAPIDestinationService.getInitialSinkConfig({
              dataSchema,
              isEditMode: true,
              sinkConfig: defaultSinkConfig,
              connectionCategory: CONNECTION_CATEGORY_TEMPLATIZED_API,
            }),
          );

          if (error) console.warn(error.message);

          sinkConfig = data.sinkConfig;
          sinkMapping = data.sinkMapping;

          if (!sinkConfig?.vendor_id) {
            sinkConfig.sink_type = connection;
            sinkConfig.vendor_endpoint_id = sink?.vendor_endpoint || sink?.vendor_endpoint_id;
            sinkConfig = { ...sinkConfig, ...sink?.template_config };
          }

          const decoded = restAPIDestinationService.getDecodedSink({
            sink,
            sinkConfig,
            credentials,
            fields: getSinkConfigFields(connection),
          });

          updateFormData({
            sinkMapping,
            dataSetId: nexset.id,
            sinkConfig: decoded.sinkConfig,
            selectedConnection: connection,
            scriptCredentialsData: decoded.scriptCredentialsData,
          });

          break;
        }
        case CONNECTION_CATEGORY_NOSQL: {
          initFormData = {
            ...initFormData,
            ...initializeFormToNoSQLCategory({
              resource: sink,
              config: sinkConfig,
              prefix: 'sinkConfig',
            }),
          };
          break;
        }
        case CONNECTION_CATEGORY_FILE: {
          const { config, selectedBucket } = flowService.initializeFormToFileCategory(sinkConfig);
          initFormData = {
            ...initFormData,
            selectedBucket,
            sinkConfig: config,
          };

          break;
        }
      }

      updateFormData({ ...initFormData, sinkConfig });
      setStatus('finished');
    } catch (e) {
      console.error('fetchDataEditSinkFlow: Error fetching data:', e.message);
    }
  }, [
    sinkId,
    sink,
    nexset,
    savedSinkMapping,
    vendors,
    credentials,
    org,
    getNexsetSchemaById,
    getMetaInfoById,
    getSinkConfigFields,
    getConnectionCategoryByConnectorType,
    updateFormData,
  ]);

  // Stable ref so the useEffect below always calls the latest version
  // without needing initializeEditForm in its dependency array (which
  // would cause an infinite loop because the function dispatches Redux
  // actions that update its own dependencies).
  const initializeEditFormRef = useRef(initializeEditForm);
  initializeEditFormRef.current = initializeEditForm;

  useEffect(() => {
    dispatch(loadVendors());
    dispatch(loadDataCredentials(aclQueryMode));
  }, []);

  /**
   * @CreateFlow
   */
  useEffect(() => {
    if (flowId) {
      (async () => {
        /**
         * Step 1 - fetch all the necessary data and start the Form
         */
        await startCreateSink();
      })();
    }
  }, [flowId]);

  useEffect(() => {
    const { selectedConnection, credentialId } = formData;

    if (credentialId && selectedConnection && status === 'initialize-create-form') {
      (async () => {
        /**
         * Step 2 - Build the form with all the necessary information
         */
        await initializeCreateForm();
      })();
    }
  }, [status, formData?.selectedConnection, formData?.credentialId]);

  /**
   * @EditFlow
   */
  useEffect(() => {
    if (sinkId) {
      (async () => {
        /**
         * Step 1
         */
        await startEditSink();
      })();
    }
  }, [sinkId]);

  useEffect(() => {
    if (sink && status === 'initialize-edit-form') {
      (async () => {
        await initializeEditFormRef.current();
      })();
    }
  }, [status, sink]);
};
